import React from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useModal } from "react-modal-hook";
import { useHistory } from "react-router";
import { PROPERTY_INFORMATION_SECTION_CODE } from "common/constants";
import { buildLink } from "common/routing";
import { useCreatePropertyInformationSectionMutation } from "../../../../generated/graphql";
import { useStatusToasts } from "../../../../hooks/useStatusToasts";
import {
  ButtonSection,
  Container,
  ContentSection,
  HeaderSection,
  PrimaryButtons,
} from "../../../Common/__styles__/Modal";
import { Button } from "../../../Common/Buttons/Button";
import Modal from "../../../Common/Modal";
import { EditSectionFormStructure } from "./OverviewSections/EditSection";
import { SectionSettings } from "./OverviewSections/SectionSettings";

export const useCreateOverviewSectionModal = ({
  onUpdate,
}: {
  onUpdate: () => void;
}) => {
  const [showCreateOverviewSectionModal, hideCreateOverviewSectionModal] =
    useModal(() => {
      return (
        <Modal onRequestClose={hideCreateOverviewSectionModal}>
          <CreateOverviewSectionModal
            onCancel={hideCreateOverviewSectionModal}
            onUpdate={onUpdate}
          />
        </Modal>
      );
    });

  return [
    showCreateOverviewSectionModal,
    hideCreateOverviewSectionModal,
  ] as const;
};

export const CreateOverviewSectionModal = ({
  onCancel,
  onUpdate,
}: {
  onCancel: () => void;
  onUpdate: () => void;
}) => {
  const history = useHistory();
  const { addErrorToast, addSuccessToast } = useStatusToasts();
  const formMethods = useForm<EditSectionFormStructure>();
  const { handleSubmit } = formMethods;

  const [createPropertyInformationSection, { loading }] =
    useCreatePropertyInformationSectionMutation({
      onCompleted: data => {
        onUpdate();
        addSuccessToast("Section created successfully");
        history.push(
          buildLink("editSection", {
            sectionId: data.createPropertyInformationSection.id,
          }),
        );
      },
      onError: () => {
        addErrorToast("Failed to create section");
      },
    });

  const onSubmit = async (data: EditSectionFormStructure) => {
    await createPropertyInformationSection({
      variables: {
        data: {
          label: data.label,
          hiddenFromPublic: !data.displayToPublic,
        },
      },
    });
  };

  return (
    <Container>
      <HeaderSection>
        <h1>Add section</h1>
      </HeaderSection>
      <ContentSection>
        <FormProvider {...formMethods}>
          <SectionSettings code={PROPERTY_INFORMATION_SECTION_CODE.CUSTOM} />
        </FormProvider>
      </ContentSection>
      <ButtonSection>
        <PrimaryButtons>
          <Button
            size="medium"
            variant="secondary"
            onPress={onCancel}
            title="Cancel"
          />
          <Button
            size="medium"
            variant="primary"
            onPress={() => handleSubmit(onSubmit)()}
            isPending={loading}
            title="Add"
          />
        </PrimaryButtons>
      </ButtonSection>
    </Container>
  );
};
